import React, { useState, useCallback } from 'react';
import { weekdayAppointmentSlots, weekendAppointmentSlots } from '../constants';
import { API_BASE_URL, API_KEY } from '../user_constant';
import { buildVisitationInsert } from '../utils';

interface AppointmentPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onAppointmentScheduled: (appointment: { date: Date; time: string; note: string }) => void;
  employeeNumber: string;
  patientId: string;
  userRole: 'staff' | 'family';
}

type WriteResult = { success: true } | { success: false; error: string };

/**
 * Unified PUT helper for middleware writes. Returns a discriminated union so
 * callers don't have to deal with `try/catch` around `fetch`. Error messages
 * are surfaced verbatim from the server when available, falling back to a
 * generic HTTP-status message.
 */
async function performWrite(body: object, endpointFunc: string): Promise<WriteResult> {
  try {
    const res = await fetch(`${API_BASE_URL}?func=${endpointFunc}`, {
      method: 'PUT',
      headers: { 'x-api-key': API_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let parsed: any = null;
    try {
      parsed = await res.json();
    } catch {
      /* swallow — non-JSON body is treated as a generic failure below */
    }
    if (!res.ok || !parsed?.success) {
      return { success: false, error: parsed?.error || `Save failed (HTTP ${res.status}).` };
    }
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Network error.' };
  }
}

const AppointmentPanel: React.FC<AppointmentPanelProps> = ({
  isOpen,
  onClose,
  onAppointmentScheduled,
  employeeNumber,
  patientId,
  userRole,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isSaving, setIsSaving] = useState(false);

  // Per-field inline validation errors (Task 5.4)
  const [dateError, setDateError] = useState<string | null>(null);
  const [timeError, setTimeError] = useState<string | null>(null);

  // Top-level submit error banner (Task 5.7)
  const [submitError, setSubmitError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setSelectedDate(null);
    setSelectedTime(null);
    setNote('');
    setCurrentMonth(new Date());
    setDateError(null);
    setTimeError(null);
    setSubmitError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSave = useCallback(async () => {
    // Per-field validation gate (must short-circuit BEFORE any fetch).
    if (!selectedDate) {
      setDateError('Please select a date.');
      return;
    }
    if (!selectedTime) {
      setTimeError('Please select a time slot.');
      return;
    }

    setSubmitError(null);
    setIsSaving(true);

    // buildVisitationInsert routes scheduledDate through formatLocalDate(),
    // so `scheduled_date` is always the locally-picked calendar day. We do
    // NOT call toISOString() anywhere on the submit path. `created_by_employee_id`
    // is always present in the body (parseInt for staff, null for family).
    const body = buildVisitationInsert({
      patientId,
      scheduledDate: selectedDate,
      scheduledTimeSlot: selectedTime,
      notes: note.trim(),
      userRole,
      employeeNumber: employeeNumber || null,
    });

    const result = await performWrite(body, 'HtUpdateVisitation');

    setIsSaving(false);

    if (result.success) {
      setSubmitError(null);
      onAppointmentScheduled({
        date: selectedDate,
        time: selectedTime,
        note: note.trim(),
      });
      onClose();
      resetState();
    } else {
      setSubmitError(result.error || 'Failed to save the visit. Please try again.');
    }
  }, [
    selectedDate,
    selectedTime,
    note,
    onAppointmentScheduled,
    onClose,
    resetState,
    employeeNumber,
    patientId,
    userRole,
  ]);

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const blanks = Array.from({ length: firstDayOfMonth }).map((_, i) => (
      <div key={`blank-${i}`} className="p-1 text-center text-sm"></div>
    ));

    const handleDateSelect = (day: number) => {
      const newDate = new Date(year, month, day);
      if (newDate >= today) {
        setSelectedDate(newDate);
        setSelectedTime(null); // Reset time when date changes
        setDateError(null);
        setTimeError(null);
        setSubmitError(null);
      }
    };

    const days = Array.from({ length: daysInMonth }).map((_, day) => {
      const date = new Date(year, month, day + 1);
      const isPast = date < today;
      const isSelected = selectedDate && date.getTime() === selectedDate.getTime();
      const isToday = date.getTime() === today.getTime();

      return (
        <button
          key={day + 1}
          onClick={() => handleDateSelect(day + 1)}
          disabled={isPast}
          className={`w-10 h-10 rounded-full text-sm transition-colors duration-200
            ${isPast ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-primary-light'}
            ${isSelected ? 'bg-primary text-white font-bold' : ''}
            ${!isSelected && isToday ? 'bg-accent-yellow text-black font-bold' : ''}
          `}
        >
          {day + 1}
        </button>
      );
    });

    const handlePrevMonth = () =>
      setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    const handleNextMonth = () =>
      setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

    return (
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-light-gray">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={handlePrevMonth}
            className="p-2 rounded-full hover:bg-light-gray"
            aria-label="Previous month"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h3 className="font-bold text-lg text-primary">
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            onClick={handleNextMonth}
            className="p-2 rounded-full hover:bg-light-gray"
            aria-label="Next month"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 font-semibold mb-2">
          <div>Sun</div>
          <div>Mon</div>
          <div>Tue</div>
          <div>Wed</div>
          <div>Thu</div>
          <div>Fri</div>
          <div>Sat</div>
        </div>
        <div className="grid grid-cols-7 gap-1 place-items-center">
          {...blanks}
          {...days}
        </div>
      </div>
    );
  };

  const renderTimeSlots = () => {
    if (!selectedDate) return null;

    const dayOfWeek = selectedDate.getDay();
    const slots =
      dayOfWeek === 0 || dayOfWeek === 6 ? weekendAppointmentSlots : weekdayAppointmentSlots;

    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isToday = selectedDate.getTime() === today.getTime();

    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {slots.map(time => {
          let isPast = false;
          if (isToday) {
            const [hourStr, minuteStr] = time.split(/[: ]/);
            let hour = parseInt(hourStr, 10);
            if (time.includes('PM') && hour !== 12) hour += 12;
            if (time.includes('AM') && hour === 12) hour = 0;

            if (
              now.getHours() > hour ||
              (now.getHours() === hour && now.getMinutes() > parseInt(minuteStr, 10))
            ) {
              isPast = true;
            }
          }

          const isSelected = selectedTime === time;

          return (
            <button
              key={time}
              disabled={isPast}
              onClick={() => {
                setSelectedTime(time);
                setTimeError(null);
                setSubmitError(null);
              }}
              className={`p-3 rounded-lg font-semibold text-sm transition-colors border-2
                ${isPast ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' : ''}
                ${
                  !isPast && isSelected
                    ? 'bg-primary text-white border-primary'
                    : !isPast
                    ? 'bg-white hover:border-primary-light border-light-gray'
                    : ''
                }
              `}
            >
              {time}
            </button>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    // Mobile: full-viewport sheet (fixed inset-0). Desktop (md+): fills the
    // parent .side-panel slot.
    <div className="fixed inset-0 z-50 md:relative md:inset-auto md:z-auto md:h-full flex flex-col bg-light-bg">
      <header className="p-4 bg-white border-b border-light-gray flex items-center justify-between shadow-sm">
        <h2 className="text-xl font-bold text-primary">Schedule a Visit</h2>
        <button
          onClick={handleClose}
          className="p-2 rounded-full text-gray-500 hover:bg-light-gray hover:text-gray-800"
          aria-label="Close panel"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6">
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-primary mb-2">1. Select a Date</h4>
              {renderCalendar()}
              {dateError && <p className="mt-2 text-sm text-red-600">{dateError}</p>}
            </div>
            {selectedDate && (
              <>
                <div>
                  <h4 className="font-semibold text-primary mb-2">
                    2. Select an Available Time for{' '}
                    <span className="text-primary">
                      {selectedDate.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </span>
                  </h4>
                  {renderTimeSlots()}
                  {timeError && <p className="mt-2 text-sm text-red-600">{timeError}</p>}
                </div>
                <div>
                  <h4 className="font-semibold text-primary mb-2">3. Remarks (optional)</h4>
                  <textarea
                    value={note}
                    onChange={e => {
                      setNote(e.target.value);
                      setSubmitError(null);
                    }}
                    rows={4}
                    placeholder="e.g., Who is visiting, any special instructions..."
                    className="w-full p-3 border rounded-lg bg-white text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary transition-colors border-gray-300"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {submitError && (
        <div
          role="alert"
          className="mx-4 mb-2 p-3 rounded-md border border-red-300 bg-red-50 text-red-800 text-sm"
        >
          {submitError}
        </div>
      )}

      <div className="p-4 bg-white border-t border-light-gray flex justify-end items-center gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={resetState}
          className="px-4 py-2 rounded-lg font-medium bg-light-gray text-gray-700 hover:bg-gray-200 transition"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!selectedDate || !selectedTime || isSaving}
          className="px-6 py-2 rounded-lg font-medium bg-accent text-white hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Confirming...
            </>
          ) : (
            'Confirm Visit'
          )}
        </button>
      </div>
    </div>
  );
};

export default AppointmentPanel;
